/* eslint-disable ember/classic-decorator-hooks, ember/require-computed-property-dependencies, no-restricted-properties */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import EmberObject, { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  filterBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';

export default class DebtorsReportController extends Controller {
  @action
  set_reportTitle(value) {
    this._reportTitle = value;
  }

  @action
  setAsAtDate(value) {
    this.asAtDate = value;
  }

  @action
  setNewDate(value) {
    this.newDate = value;
  }

  queryParams = ['asAtDate'];

  @service
  session;

  @alias('session.school.jurisdiction')
  jurisdiction;

  today = moment().format('YYYY-MM-DD');
  @tracked asAtDate = moment().format('YYYY-MM-DD');
  @tracked
  newDate = '';
  @tracked _reportTitle = null;

  get reportTitle() {
    if (this._reportTitle !== null) {
      return this._reportTitle;
    }
    return `Debtor Overview report as at ${this.asAtDate}`;
  }

  @computed('filteredRows.@each.invoiceDebits')
  get invoiceDebitTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.invoiceDebits;
    }, 0);
  }

  @computed('filteredRows.@each.donationDebits')
  get donationDebitTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.donationDebits;
    }, 0);
  }

  @computed('filteredRows.@each.invoiceCredits')
  get invoiceCreditTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.invoiceCredits;
    }, 0);
  }

  @computed('filteredRows.@each.donationCredits')
  get donationCreditTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.donationCredits;
    }, 0);
  }

  @computed('filteredRows.@each.pia')
  get piaTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.pia;
    }, 0);
  }

  @computed('filteredRows.@each.totalBalance')
  get balanceTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.totalBalance;
    }, 0);
  }

  @computed('filteredRows.@each.debtBalance')
  get debtBalanceTotal() {
    return this.filteredRows.reduce((sum, row) => {
      return sum + row.debtBalance;
    }, 0);
  }

  @computed('jurisdiction')
  get columns() {
    return [
      {
        field: 'person.jurisdictionalId',
        label: this.jurisdiction === Jurisdiction.NZ_MOE ? 'NSN' : 'ID',
        datatype: DataType.STRING,
      },
      {
        field: 'person.lastName',
        label: 'Last Name',
        datatype: DataType.STRING,
      },
      {
        field: 'person.firstName',
        label: 'First Name',
        datatype: DataType.STRING,
      },
      {
        field: 'person.homeroom.name',
        label: 'Room',
        datatype: DataType.STRING,
      },
      {
        field: 'person.currentYearLevel',
        label: 'Year Level',
        datatype: DataType.INTEGER,
      },
      {
        field: 'invoiceDebits',
        label: 'Unpaid Invoices',
        datatype: DataType.FLOAT,
      },
      {
        field: 'donationDebits',
        label: 'Unpaid Donations',
        datatype: DataType.FLOAT,
      },
      {
        field: 'invoiceCredits',
        label: 'Invoice Credits',
        datatype: DataType.FLOAT,
      },
      {
        field: 'donationCredits',
        label: 'Donation Credits',
        datatype: DataType.FLOAT,
      },
      {
        field: 'pia',
        label: 'Unallocated PIA',
        datatype: DataType.FLOAT,
      },
      {
        field: 'totalBalance',
        label: 'Balance',
        datatype: DataType.FLOAT,
      },
      {
        field: 'debtBalance',
        label: 'Debt Balance',
        datatype: DataType.FLOAT,
      },
    ];
  }

  @tracked
  filters = null;

  @computed('filters.@each.value', 'model.debtors.[]')
  get filteredRows() {
    let filtered = this.model.debtors;
    if (isArray(this.filters)) {
      this.filters.forEach((filter) => {
        let datatype = findBy(this.columns, 'field', filter.field).datatype;
        filtered = filtered.filter((row) => {
          let rowValue;
          if (datatype === DataType.STRING) {
            // String filtering requires a string; `undefined` won't do.
            rowValue = row.get(filter.field) ?? '';
          } else {
            rowValue = row.get(filter.field);
          }
          return filterValue(rowValue, datatype, filter.value);
        });
      });
    }
    return filtered;
  }

  // allPeopleLoaded is used as a trigger to re-sort the table once all Person
  // records have been loaded (i.e. for non-current students).
  @computed('model.debtors.@each.personLoaded')
  get allPeopleLoaded() {
    return this.model.debtors.every((debtor) => debtor.get('personLoaded'));
  }

  @tracked sortBy = 'person.lastName';
  @tracked sortDir = 'asc';

  @computed('filteredRows.[]', 'sortBy', 'sortDir', 'allPeopleLoaded')
  get sortedFilteredRows() {
    let sortField = this.sortBy;
    let sorted = this.filteredRows
      .slice()
      .sort((a, b) =>
        a.get(sortField) > b.get(sortField)
          ? 1
          : a.get(sortField) < b.get(sortField)
            ? -1
            : 0,
      );
    if (this.sortDir === 'desc') {
      return sorted.reverse();
    }
    return sorted;
  }

  init() {
    super.init(...arguments);
    this.newDate = this.asAtDate;
  }

  @action
  setFilter(field, value) {
    if (!isArray(this.filters)) {
      // Strip out any empty filters
      this.filters = [];
    }
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      this.filters = [
        ...this.filters,
        EmberObject.create({
          field,
          value,
        }),
      ];
    } else {
      filter.set('value', value);
    }

    this.filters = filterBy(this.filters, 'value');
  }

  reset() {
    this.filters = [];
    this.newDate = this.asAtDate;
  }
}
